import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const RequirementDetailPanel = ({ requirement, onClose, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(requirement || {});

  if (!requirement) return null;

  const statusOptions = [
    { value: 'Proposed', label: 'Proposed' },
    { value: 'Analyzing', label: 'Analyzing' },
    { value: 'Fit', label: 'Fit' },
    { value: 'Gap', label: 'Gap' },
    { value: 'Approved', label: 'Approved' }
  ];

  const complexityOptions = [
    { value: 'Low', label: 'Low' },
    { value: 'Medium', label: 'Medium' },
    { value: 'High', label: 'High' }
  ];

  const assigneeOptions = [
    { value: '', label: 'Unassigned' },
    { value: 'Sarah Chen', label: 'Sarah Chen' },
    { value: 'Michael Rodriguez', label: 'Michael Rodriguez' },
    { value: 'David Kim', label: 'David Kim' },
    { value: 'Emma Thompson', label: 'Emma Thompson' }
  ];

  const handleSave = () => {
    onUpdate(editData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditData(requirement);
    setIsEditing(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Proposed': return 'bg-muted text-muted-foreground';
      case 'Analyzing': return 'bg-warning text-warning-foreground';
      case 'Fit': return 'bg-success text-success-foreground';
      case 'Gap': return 'bg-destructive text-destructive-foreground';
      case 'Approved': return 'bg-primary text-primary-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="w-96 bg-card border-l border-border h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground">
          Requirement Details
        </h3>
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            iconName={isEditing ? "X" : "Edit"}
            onClick={isEditing ? handleCancel : () => setIsEditing(true)}
          >
            {isEditing ? 'Cancel' : 'Edit'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
          />
        </div>
      </div>
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Basic Information */}
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-3">Basic Information</h4>
          
          {isEditing ? (
            <div className="space-y-4">
              <Input
                label="Title"
                value={editData?.title}
                onChange={(e) => setEditData({...editData, title: e?.target?.value})}
              />
              
              <Input
                label="Description"
                value={editData?.description}
                onChange={(e) => setEditData({...editData, description: e?.target?.value})}
              />
              
              <Select
                label="Status"
                options={statusOptions}
                value={editData?.status}
                onChange={(value) => setEditData({...editData, status: value})}
              />
              
              <Select
                label="Complexity"
                options={complexityOptions}
                value={editData?.complexity}
                onChange={(value) => setEditData({...editData, complexity: value})}
              />
              
              <Select
                label="Assignee"
                options={assigneeOptions}
                value={editData?.assignee || ''}
                onChange={(value) => setEditData({...editData, assignee: value})}
              />
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Title</label>
                <p className="text-sm text-foreground mt-1">{requirement?.title}</p>
              </div>
              
              <div>
                <label className="text-xs font-medium text-muted-foreground">Description</label>
                <p className="text-sm text-foreground mt-1">{requirement?.description}</p>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Status</label>
                  <div className="mt-1">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(requirement?.status)}`}>
                      {requirement?.status}
                    </span>
                  </div>
                </div>
                
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Complexity</label>
                  <p className="text-sm text-foreground mt-1">{requirement?.complexity}</p>
                </div>
              </div>
              
              <div>
                <label className="text-xs font-medium text-muted-foreground">Assignee</label>
                <p className="text-sm text-foreground mt-1">
                  {requirement?.assignee || 'Unassigned'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* SAP Standard Assessment */}
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-3">SAP Standard Assessment</h4>
          
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-muted-foreground">Fit Percentage</span>
                <span className="text-sm font-medium text-foreground">{requirement?.sapFitPercentage}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${
                    requirement?.sapFitPercentage >= 80 ? 'bg-success' :
                    requirement?.sapFitPercentage >= 60 ? 'bg-warning' : 'bg-destructive'
                  }`}
                  style={{ width: `${requirement?.sapFitPercentage}%` }}
                />
              </div>
            </div>
            
            <div>
              <label className="text-xs font-medium text-muted-foreground">Fit/Gap Reasoning</label>
              <div className="mt-1 p-3 bg-muted/50 rounded-lg">
                <p className="text-sm text-foreground">{requirement?.fitGapReasoning}</p>
              </div>
            </div>
            
            <div>
              <label className="text-xs font-medium text-muted-foreground">Recommended Action</label>
              <p className="text-sm text-foreground mt-1">{requirement?.recommendedAction}</p>
            </div>
          </div>
        </div>

        {/* Effort Estimation */}
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-3">Effort Estimation</h4>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Effort (Days)</label>
              <p className="text-sm text-foreground mt-1">{requirement?.effortDays || 'TBD'}</p>
            </div>
            
            <div>
              <label className="text-xs font-medium text-muted-foreground">Cost Estimate</label>
              <p className="text-sm text-foreground mt-1">{requirement?.costEstimate || 'TBD'}</p>
            </div>
          </div>
        </div>

        {/* RICEFW Recommendations */}
        {requirement?.hasRicefw && (
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3">RICEFW Recommendations</h4>
            
            <div className="space-y-2">
              {requirement?.ricefwItems?.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                  <div className="flex items-center space-x-2">
                    <Icon name="Code" size={14} className="text-accent" />
                    <span className="text-sm text-foreground">{item?.type}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{item?.complexity}</span>
                </div>
              )) || (
                <p className="text-sm text-muted-foreground">No RICEFW items identified</p>
              )}
            </div>
          </div>
        )}

        {/* Activity Timeline */}
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-3">Activity Timeline</h4>
          
          <div className="space-y-3">
            {requirement?.activities?.map((activity, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground">{activity?.action}</p>
                  <p className="text-xs text-muted-foreground">
                    {activity?.user} • {activity?.timestamp}
                  </p>
                </div>
              </div>
            )) || (
              <p className="text-sm text-muted-foreground">No activity recorded</p>
            )}
          </div>
        </div>
      </div>
      {/* Footer Actions */}
      {isEditing && (
        <div className="p-4 border-t border-border">
          <div className="flex items-center space-x-2">
            <Button
              variant="default"
              size="sm"
              onClick={handleSave}
              className="flex-1"
            >
              Save Changes
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequirementDetailPanel;